import differenceInYears from 'date-fns/differenceInYears';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';

import {
  BIRTH_DATE_DISPLAY_FORMAT,
  BIRTH_DATE_SERVER_FORMAT,
} from 'consts/dataFormatConsts';
import errorMessages from 'consts/errorMessageConsts';
import fieldNames from 'consts/fieldNameConsts';

import loggerHelper from 'utils/logger/loggerHelper';

const { DATE_OF_BIRTH } = fieldNames;

export const getDateFromString = (dateString, inputFormat) => {
  try {
    const parsed = parse(dateString, inputFormat, new Date());

    return isValid(parsed) ? parsed : undefined;
  } catch {
    loggerHelper({ message: 'Date input format error', info: dateString });
  }
};

/**
 * Safe formatting only when a date is valid,
 * otherwise return nothing
 * @param {string} dateString
 */
export const parseAndFormatDate = (dateString, inputFormat, outputFormat) => {
  const parsed = getDateFromString(dateString, inputFormat);

  return (parsed && format(parsed, outputFormat)) || undefined;
};

export const getDisplayFormatFromServer = dateString =>
  parseAndFormatDate(
    dateString,
    BIRTH_DATE_SERVER_FORMAT,
    BIRTH_DATE_DISPLAY_FORMAT
  );

export const getServerFormatFromDisplay = dateString =>
  parseAndFormatDate(
    dateString,
    BIRTH_DATE_DISPLAY_FORMAT,
    BIRTH_DATE_SERVER_FORMAT
  );

export const getServerFormat = dateString => {
  if (!dateString) return;

  // Check if this is already in server format first, otherwise format it
  return (
    parseAndFormatDate(
      dateString,
      BIRTH_DATE_SERVER_FORMAT,
      BIRTH_DATE_SERVER_FORMAT
    ) || getServerFormatFromDisplay(dateString)
  );
};

export const isBetween18And30 = val => {
  const age = getAgeInYears(val);
  return age > 18 && age < 30;
};

/**
 * Get the age in years based on the current date and the full
 * birth date string. Returns the age in years.
 *
 * @param {string} birthDateString    The full birth date string
 */
function getAgeInYears(birthDateString) {
  const birthDate = getDateFromString(
    birthDateString,
    BIRTH_DATE_SERVER_FORMAT
  );
  const diffInYears = differenceInYears(new Date(), birthDate);

  return diffInYears;
}

function is18OrOver(val) {
  const age = getAgeInYears(val);
  return age > 17;
}

function isUnder121(val) {
  const age = getAgeInYears(val);
  return age <= 120;
}

export default {
  /**
   *  Birthday input
   * Expected Value: '1992-18-08'
   */
  [DATE_OF_BIRTH]: {
    name: DATE_OF_BIRTH,
    label: 'Date of birth',
    placeholder: BIRTH_DATE_DISPLAY_FORMAT.toLowerCase(),
    autoComplete: 'bday',
    type: 'date',
    mask: BIRTH_DATE_DISPLAY_FORMAT.toLowerCase().replace(/\w/g, '9'),
    validationRules: [
      {
        isValid: val => val && val.length,
        message: errorMessages.BLANK_BDAY_ERROR_MESSAGE,
      },
      {
        isValid: val => getDateFromString(val, BIRTH_DATE_SERVER_FORMAT),
        message: errorMessages.GENERAL_BDAY_ERROR_MESSAGE,
      },
      {
        isValid: val => /[0-9]{8}/g.test(val.replace(/-| |\//g, '')),
        message: errorMessages.GENERAL_BDAY_ERROR_MESSAGE,
      },
      {
        isValid: is18OrOver,
        message: errorMessages.BDAY_UNDER_18_ERROR_MESSAGE,
      },
      {
        isValid: isUnder121,
        message: errorMessages.BDAY_OVER_120_ERROR_MESSAGE,
      },
    ],
    getServerFormat,
    getInputFormat: val => getDisplayFormatFromServer(val),
  },
};
